// @ts-nocheck

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import AsyncSelect from "react-select/async";
import {Department} from "../../api/platform/departments";
import {useDepartmentApi} from "../../api/platform/useDepartments";
import {reactSelectClassNames, reactSelectStyles} from "../react-select/styling";
import {noop} from "../misc/noop";
import {
    ClassNamesConfig,
    FormatOptionLabelMeta,
    MultiValueProps,
    SelectInstance,
    StylesConfig
} from "react-select";
import {nilObjectID} from "../misc/nilObjectID";


type Option = {
    id: string;
    label: string;
    department: Department;
}


type DepartmentSelectV2Props = {
    rpp?: number
    clientID?: string | null | undefined
    initialIDs?: string[] | null | undefined
    onChange?: (selected: Department[]) => void
    noDepartmentOption?: boolean
}




export const DepartmentSelectV2Multi = ({
                                       rpp = 20,
                                       clientID = null,
                                       initialIDs = null,
                                       onChange = noop,
                                       noDepartmentOption = false,

                                   }: DepartmentSelectV2Props) => {

    const deptApi = useDepartmentApi();

    const [selected, setSelected] = useState<Option[]>([])
    const [loading, setLoading] = useState(true)

    const [initialOptions, setInitialOptions] = useState<Option[]|null>(null)

    const ref = useRef<SelectInstance<Option[]>>(null)

    const prevClientID = useRef<string|null>(null)

    useEffect(() => {
        prevClientID.current = clientID
    }, [clientID]);

    useEffect(() => {

        if(loading) return
        if(!clientID) return

        const options = selected.map(s => s.department).map(d => ({
            id: d.id,
            label: d.name,
            department: d,
            value: d.id,
        }))

        if(prevClientID.current && clientID != prevClientID.current){
            ref.current?.clearValue()

            options.forEach(o => ref.current?.selectOption(o))

            setSelected(options)
        }
    }, [clientID, loading, prevClientID.current, ref.current]);


    const loadDepartments = useCallback(async () => {
        if(!initialIDs) return

        const promises = initialIDs.filter(id => id.trim()).map((id) => {
            return deptApi.get(id).then(([data]) => data)
        })

        const departments = await Promise.all(promises)

        const options = departments.filter(d => d?.clientID == clientID).map(d => ({
            id: d.id,
            label: d.name,
            department: d,
            value: d.id,
        }))

        setInitialOptions(options)
        setSelected(options)

    }, [initialIDs, deptApi, clientID]);



    useEffect(() => {
        loadDepartments().finally(() => setLoading(false))
    }, [loadDepartments]);

    useEffect(() => {
        if(loading) return
        onChange(selected.map(o => o.department))
    }, [selected, loading]);




    const onSelect = useCallback((options?: Option[]) => {
        if(loading){
            return
        }
        if (options) {
            setSelected(options)
            return
        }

        setSelected([])
    }, [onChange, selected]);



    const promiseOptions = useCallback(async (search: string) => {
        const [results, err] = await deptApi.list({clientID: clientID ?? "", search, limit: rpp});
        if (err || !results) return [];
        const items = results.items.map((d) => ({
            id: d.id,
            label: d.name,
            value: d.id,
            department: d,
        }))

        items.unshift({
            id: nilObjectID,
            label: "No Department",
            department: {
                id: nilObjectID,
                name: "No Department",
            },
            value: nilObjectID,
        })

        return items;
    }, [deptApi, rpp, clientID]);

    if (loading) {
        return null
    }

    const formatOptionLabel = (option: Option, meta: FormatOptionLabelMeta<Option>) => {
        return option.label
    }

    const MultiValue = (props: MultiValueProps<Option>) => {

        if(selected.length == 1) {
            return <div>{selected[0].label}</div>
        }
        return (
            <div className="[&:not(:first-child)]:hidden">{selected.length} Departments Selected</div>
        );
    };

    if(initialOptions == null){
        return null
    }

    if(loading){
        return null
    }



    return (
        <>
        <AsyncSelect<Option[]>
            ref={ref}
            key={clientID}
            classNames={reactSelectClassNames as ClassNamesConfig<Option[]>}
                     styles={reactSelectStyles as StylesConfig<Option[]>}
                     placeholder="Select Departments"
                     defaultValue={initialOptions}
                     value={selected}
                     onChange={onSelect}
                     isMulti
                     loadOptions={promiseOptions}
                     defaultOptions
                     formatOptionLabel={formatOptionLabel}
                     isClearable/>
        </>
    )
};
